// src/pages/duplicateSentData/EditDuplicateModal.jsx

import React, {useState, useEffect} from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Grid,
  Alert,
  Box,
  Typography,
  CircularProgress,
} from '@mui/material';
import {
  Save as SaveIcon,
  CheckCircle as ValidateIcon,
  Error as ErrorIcon,
} from '@mui/icons-material';
import PropTypes from 'prop-types';

const EditDuplicateModal = ({
  open,
  onClose,
  duplicate,
  onUpdate,
  onValidate,
}) => {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    age: '',
    country: '',
    marks: '',
    MPC_4: '',
    data_source: '',
  });

  const [validationStatus, setValidationStatus] = useState(null);
  const [isValidating, setIsValidating] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [errors, setErrors] = useState({});

  // Initialize form data when duplicate changes
  useEffect(() => {
    if (duplicate && duplicate.duplicate_data) {
      setFormData({
        fullName: duplicate.duplicate_data.fullName || '',
        email: duplicate.duplicate_data.email || '',
        phone: duplicate.duplicate_data.phone || '',
        age: duplicate.duplicate_data.age || '',
        country: duplicate.duplicate_data.country || '',
        marks: duplicate.duplicate_data.marks || '',
        MPC_4: duplicate.duplicate_data.MPC_4 || '',
        data_source: duplicate.duplicate_data.data_source || '',
      });
    }
    setValidationStatus(null);
    setErrors({});
  }, [duplicate]);

  // Handle form field changes
  const handleFieldChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Clear validation status when user makes changes
    if (validationStatus) {
      setValidationStatus(null);
    }

    // Clear field error
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: '',
      }));
    }
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};

    if (!formData.fullName.trim()) {
      newErrors.fullName = 'Full name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }

    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle validate button click
  const handleValidate = async () => {
    if (!validateForm()) {
      return;
    }

    setIsValidating(true);
    try {
      const isValid = await onValidate(duplicate.id, formData);
      setValidationStatus(isValid ? 'valid' : 'invalid');
    } catch (error) {
      setValidationStatus('error');
    } finally {
      setIsValidating(false);
    }
  };

  // Handle save button click
  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    setIsSaving(true);
    try {
      const success = await onUpdate(duplicate.id, {
        duplicate_data: {
          ...duplicate.duplicate_data,
          ...formData,
        },
        email: formData.email,
        phone: formData.phone,
        MPC_4: formData.MPC_4,
        data_source: formData.data_source,
      });

      if (success) {
        onClose();
      }
    } catch (error) {
      console.error('Error saving duplicate:', error);
    } finally {
      setIsSaving(false);
    }
  };

  // Handle validate and save button click
  const handleValidateAndSave = async () => {
    if (!validateForm()) {
      return;
    }

    setIsSaving(true);
    try {
      // First validate
      const isValid = await onValidate(duplicate.id, formData);

      if (isValid) {
        // If validation passes, save the changes
        const success = await onUpdate(duplicate.id, {
          duplicate_data: {
            ...duplicate.duplicate_data,
            ...formData,
          },
          email: formData.email,
          phone: formData.phone,
          MPC_4: formData.MPC_4,
          data_source: formData.data_source,
        });

        if (success) {
          onClose();
        }
      }
    } catch (error) {
      console.error('Error validating and saving duplicate:', error);
    } finally {
      setIsSaving(false);
    }
  };

  // Handle close
  const handleClose = () => {
    setValidationStatus(null);
    setErrors({});
    onClose();
  };

  if (!duplicate) {
    return null;
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth='md' fullWidth>
      <DialogTitle>
        Edit Duplicate Data
        <Typography variant='body2' color='textSecondary' sx={{mt: 1}}>
          ID: {duplicate.id} | Status: {duplicate.status}
        </Typography>
      </DialogTitle>

      <DialogContent>
        <Box sx={{mb: 3}}>
          <Alert severity='info'>
            Edit the duplicate data below. Use the &quot;Validate&quot; button
            to check if the edited data is no longer a duplicate before saving.
          </Alert>
        </Box>

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label='Full Name'
              value={formData.fullName}
              onChange={(e) => handleFieldChange('fullName', e.target.value)}
              error={!!errors.fullName}
              helperText={errors.fullName}
              required
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label='Email'
              type='email'
              value={formData.email}
              onChange={(e) => handleFieldChange('email', e.target.value)}
              error={!!errors.email}
              helperText={errors.email}
              required
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label='Phone'
              value={formData.phone}
              onChange={(e) => handleFieldChange('phone', e.target.value)}
              error={!!errors.phone}
              helperText={errors.phone}
              required
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label='Age'
              value={formData.age}
              onChange={(e) => handleFieldChange('age', e.target.value)}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label='Country'
              value={formData.country}
              onChange={(e) => handleFieldChange('country', e.target.value)}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label='Marks'
              value={formData.marks}
              onChange={(e) => handleFieldChange('marks', e.target.value)}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label='MPC_4'
              multiline
              rows={3}
              value={formData.MPC_4}
              onChange={(e) => handleFieldChange('MPC_4', e.target.value)}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label='Data Source'
              value={formData.data_source}
              onChange={(e) => handleFieldChange('data_source', e.target.value)}
              helperText='Source of the lead data (e.g., website, campaign, API)'
            />
          </Grid>
        </Grid>

        {/* Validation Status */}
        {validationStatus && (
          <Box sx={{mt: 3}}>
            {validationStatus === 'valid' && (
              <Alert severity='success' icon={<ValidateIcon />}>
                Validation successful! The data is no longer a duplicate and can
                be transferred to free data.
              </Alert>
            )}
            {validationStatus === 'invalid' && (
              <Alert severity='warning' icon={<ErrorIcon />}>
                The data is still a duplicate. Please modify the fields to make
                it unique.
              </Alert>
            )}
            {validationStatus === 'error' && (
              <Alert severity='error' icon={<ErrorIcon />}>
                Validation failed. Please try again.
              </Alert>
            )}
          </Box>
        )}

        {/* Original Data Reference */}
        <Box sx={{mt: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1}}>
          <Typography variant='subtitle2' gutterBottom>
            Original Data (for reference):
          </Typography>
          <Typography variant='body2' color='textSecondary'>
            <strong>Name:</strong> {duplicate.original_data?.fullName || 'N/A'}{' '}
            |<strong> Email:</strong> {duplicate.original_data?.email || 'N/A'}{' '}
            |<strong> Phone:</strong> {duplicate.original_data?.phone || 'N/A'}
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={isSaving}>
          Cancel
        </Button>

        <Button
          variant='outlined'
          startIcon={
            isValidating ? <CircularProgress size={20} /> : <ValidateIcon />
          }
          onClick={handleValidate}
          disabled={isValidating || isSaving}
        >
          {isValidating ? 'Validating...' : 'Validate'}
        </Button>

        <Button
          variant='outlined'
          startIcon={isSaving ? <CircularProgress size={20} /> : <SaveIcon />}
          onClick={handleSave}
          disabled={isSaving || validationStatus === 'invalid'}
        >
          {isSaving ? 'Saving...' : 'Save Changes'}
        </Button>

        <Button
          variant='contained'
          startIcon={
            isSaving ? <CircularProgress size={20} /> : <ValidateIcon />
          }
          onClick={handleValidateAndSave}
          disabled={isSaving || isValidating}
        >
          {isSaving ? 'Processing...' : 'Validate & Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

EditDuplicateModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  duplicate: PropTypes.object,
  onUpdate: PropTypes.func.isRequired,
  onValidate: PropTypes.func.isRequired,
};

export default EditDuplicateModal;
